import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { crmListProspects, listCandidates, crmDeals } from "@/lib/api";
import { hasPerm } from "@/lib/auth";
import { Search, Building2, User, Briefcase, CornerDownLeft } from "lucide-react";

const money = (n) => {
  const v = Number(n || 0);
  if (!v) return "$0";
  return "$" + v.toLocaleString();
};

/**
 * Global command search across existing Prospects, Candidates and Deals data.
 * Reuses existing list endpoints only (no new backend). Cmd/Ctrl+K opens it.
 */
export const GlobalSearch = ({ token, onOpenProspect, onOpenCandidate }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [deals, setDeals] = useState([]);
  const loadingRef = useRef(false);

  // Cmd/Ctrl+K shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const loadData = useCallback(async () => {
    if (loaded || loadingRef.current) return;
    loadingRef.current = true;
    try {
      const canCandidates = hasPerm("candidates.view");
      const [p, c, d] = await Promise.all([
        crmListProspects(token, { view: "all", page: 1, page_size: 500 }).catch(() => ({ prospects: [] })),
        canCandidates ? listCandidates(token, {}).catch(() => ({ candidates: [] })) : Promise.resolve({ candidates: [] }),
        crmDeals(token).catch(() => ({ deals: [] })),
      ]);
      setProspects(p.prospects || []);
      setCandidates(c.candidates || []);
      setDeals(d.deals || []);
      setLoaded(true);
    } finally {
      loadingRef.current = false;
    }
  }, [token, loaded]);

  useEffect(() => { if (open) loadData(); }, [open, loadData]);

  const q = query.trim().toLowerCase();
  const results = useMemo(() => {
    if (!q) return { prospects: [], candidates: [], deals: [] };
    const match = (hay) => hay.toLowerCase().includes(q);
    const pr = (prospects || []).filter((p) =>
      match([p.name, p.company_name, p.email, p.job_title].filter(Boolean).join(" "))
    ).slice(0, 6);
    const ca = (candidates || []).filter((c) =>
      match([c.name, c.current_position, c.email, (c.skills || []).join(" ")].filter(Boolean).join(" "))
    ).slice(0, 6);
    const de = (deals || []).filter((d) =>
      match([d.company, d.contact].filter(Boolean).join(" "))
    ).slice(0, 6);
    return { prospects: pr, candidates: ca, deals: de };
  }, [q, prospects, candidates, deals]);

  const close = () => { setOpen(false); setQuery(""); };
  const pickProspect = (id) => { close(); onOpenProspect && onOpenProspect(id); };
  const pickCandidate = (id) => { close(); onOpenCandidate && onOpenCandidate(id); };

  const hasAny = results.prospects.length || results.candidates.length || results.deals.length;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="global-search-trigger"
        className="group inline-flex items-center gap-2 h-9 w-full max-w-xs rounded-md border border-border bg-muted/40 px-3 text-sm text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Open global search"
      >
        <Search size={15} />
        <span className="hidden sm:inline">Search prospects, candidates, deals…</span>
        <span className="sm:hidden">Search…</span>
        <kbd className="ml-auto hidden md:inline-flex items-center gap-0.5 rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          ⌘K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent hideDefaultClose className="p-0 overflow-hidden max-w-xl bg-card border-border">
          <Command shouldFilter={false} className="bg-card">
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder="Search prospects, candidates, deals…"
              data-testid="global-search-input"
            />
            <CommandList className="max-h-[60vh]">
              {!q && (
                <div className="py-8 text-center text-sm text-muted-foreground" data-testid="global-search-hint">
                  Type a company, name, role, or email to jump to a record.
                </div>
              )}
              {q && !hasAny && (
                <CommandEmpty>No matches. Try a company name, role, or email.</CommandEmpty>
              )}

              {results.prospects.length > 0 && (
                <CommandGroup heading="Prospects">
                  {results.prospects.map((p) => (
                    <CommandItem key={`p-${p.id}`} value={`p-${p.id}`} onSelect={() => pickProspect(p.id)}
                      data-testid="global-search-result-prospect"
                      className="cursor-pointer aria-selected:bg-muted">
                      <Building2 size={15} className="mr-2 text-[hsl(var(--chart-2))]" />
                      <div className="min-w-0">
                        <div className="truncate text-foreground">{p.name || p.company_name || "Prospect"}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {[p.company_name, p.status].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <CornerDownLeft size={13} className="ml-auto opacity-0 group-aria-selected:opacity-60" />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {results.candidates.length > 0 && (
                <CommandGroup heading="Candidates">
                  {results.candidates.map((c) => (
                    <CommandItem key={`c-${c.id}`} value={`c-${c.id}`} onSelect={() => pickCandidate(c.id)}
                      data-testid="global-search-result-candidate"
                      className="cursor-pointer aria-selected:bg-muted">
                      <User size={15} className="mr-2 text-[hsl(var(--chart-3))]" />
                      <div className="min-w-0">
                        <div className="truncate text-foreground">{c.name || "Candidate"}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {[c.current_position, c.status].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {results.deals.length > 0 && (
                <CommandGroup heading="Deals">
                  {results.deals.map((d) => (
                    <CommandItem key={`d-${d.id}`} value={`d-${d.id}`} onSelect={() => pickProspect(d.prospect_id)}
                      data-testid="global-search-result-deal"
                      className="cursor-pointer aria-selected:bg-muted">
                      <Briefcase size={15} className="mr-2 text-[hsl(var(--chart-4))]" />
                      <div className="min-w-0">
                        <div className="truncate text-foreground">{d.company || "Deal"}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {[d.contact, `${money(d.monthly_value)}/mo`, d.proposal_status].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
};
